<div
  cdkOverlayOrigin
  #overlayOrigin="cdkOverlayOrigin"
  class="cue-datetimepicker__field"
  [class.cue-datetimepicker__field--active]="isActive"
>
  <input
    #dateTimeInput
    class="cue-datetimepicker__input"
    type="text"
    role="combobox"
    autocomplete="off"
    [attr.aria-controls]="popupId"
    [attr.aria-expanded]="isOpen"
    aria-haspopup="dialog"
    [attr.aria-invalid]="inputIsIncomplete()"
    [attr.placeholder]="resolvedPlaceholder()"
    [attr.tabindex]="isDisabled() ? -1 : tabindex()"
    [disabled]="isDisabled()"
    [readOnly]="readonly() || readOnlyInput()"
    [title]="adaptiveTitle()"
    [value]="inputText()"
    (blur)="handleInputBlur($event)"
    (focus)="handleInputFocus($event)"
    (input)="handleInput($event)"
    (keydown)="handleInputKeydown($event)"
    (wheel)="handleInputWheel($event)"
  />

  @if (clearButton() && inputText() && !isDisabled() && !readonly()) {
    <button
      class="cue-datetimepicker__icon-button cue-datetimepicker__clear-button"
      type="button"
      [attr.aria-label]="effectiveMessages().clear"
      (click)="clearValue($event)"
    >
      <kendo-svg-icon [icon]="xIcon" />
    </button>
  }

  <button
    class="cue-datetimepicker__icon-button cue-datetimepicker__toggle-button"
    type="button"
    [attr.aria-label]="effectiveMessages().open"
    [attr.aria-expanded]="isOpen"
    [disabled]="isDisabled() || readonly()"
    (click)="requestToggle()"
  >
    <kendo-svg-icon [icon]="!timeOnly() && activeTab() === 'date' ? calendarIcon : clockIcon" />
  </button>
</div>

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="popupOrigin() ?? overlayOrigin"
  [cdkConnectedOverlayOpen]="isOpen"
  [cdkConnectedOverlayPositions]="overlayPositions"
  [cdkConnectedOverlayPanelClass]="popupPanelClasses()"
  [cdkConnectedOverlayFlexibleDimensions]="true"
  [cdkConnectedOverlayPush]="true"
  [cdkConnectedOverlayHasBackdrop]="true"
  cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
  (backdropClick)="requestClose()"
>
  <section
    #popup
    cdkTrapFocus
    [cdkTrapFocusAutoCapture]="true"
    class="cue-datetimepicker__popup"
    [class.cue-datetimepicker__popup--time-only]="timeOnly()"
    [class.cue-datetimepicker__popup--animated]="popupHasAnimation()"
    [class.cue-datetimepicker__popup--adaptive]="isAdaptive()"
    [class.cue-datetimepicker__popup--infinite]="calendarType() === 'infinite'"
    [id]="popupId"
    role="dialog"
    tabindex="-1"
    [attr.aria-label]="adaptiveTitle() || effectiveMessages().open"
    (keydown)="handlePopupKeydown($event)"
  >
    @if (isAdaptive() && (adaptiveTitle() || adaptiveSubtitle())) {
      <header class="cue-datetimepicker__adaptive-header">
        @if (adaptiveTitle()) {
          <strong>{{ adaptiveTitle() }}</strong>
        }
        @if (adaptiveSubtitle()) {
          <span>{{ adaptiveSubtitle() }}</span>
        }
      </header>
    }

    @if (timeOnly()) {
      <div class="cue-datetimepicker__time-heading">{{ pendingTimeLabel() }}</div>
    }

    @if (!timeOnly()) {
      <div class="cue-datetimepicker__tabs" role="tablist">
        <button
          class="cue-datetimepicker__tab"
          type="button"
          role="tab"
          [class.cue-datetimepicker__tab--active]="activeTab() === 'date'"
          [attr.aria-selected]="activeTab() === 'date'"
          (click)="changeTab('date')"
        >
          <kendo-svg-icon [icon]="calendarIcon" />
          {{ effectiveMessages().dateTab }}
        </button>
        <button
          class="cue-datetimepicker__tab"
          type="button"
          role="tab"
          [class.cue-datetimepicker__tab--active]="activeTab() === 'time'"
          [attr.aria-selected]="activeTab() === 'time'"
          (click)="changeTab('time')"
        >
          <kendo-svg-icon [icon]="clockIcon" />
          {{ effectiveMessages().timeTab }}
        </button>
      </div>
    }

    <div class="cue-datetimepicker__content">
      @if (!timeOnly() && activeTab() === 'date') {
        <div
          class="cue-datetimepicker__calendar"
          [class.cue-datetimepicker__calendar--navigating]="calendarNavigating()"
          role="tabpanel"
          (wheel)="handleCalendarWheel($event)"
        >
          @if (headerTemplate(); as customHeader) {
            <ng-container [ngTemplateOutlet]="customHeader" />
          } @else {
            <div class="cue-datetimepicker__calendar-header">
              <button
                class="cue-datetimepicker__navigation-button"
                type="button"
                [attr.aria-label]="effectiveMessages().previousPeriod"
                (click)="navigateCalendar(-1)"
              >
                <kendo-svg-icon [icon]="chevronLeftIcon" />
              </button>
              <button
                class="cue-datetimepicker__period-button"
                type="button"
                [disabled]="calendarView() === 'century'"
                (click)="showHigherCalendarView()"
              >
                @if (headerTitleTemplate(); as customTitle) {
                  <ng-container
                    [ngTemplateOutlet]="customTitle"
                    [ngTemplateOutletContext]="{ $implicit: calendarHeading(), title: calendarHeading() }"
                  />
                } @else {
                  {{ calendarHeading() }}
                }
              </button>
              <button
                class="cue-datetimepicker__navigation-button"
                type="button"
                [attr.aria-label]="effectiveMessages().nextPeriod"
                (click)="navigateCalendar(1)"
              >
                <kendo-svg-icon [icon]="chevronRightIcon" />
              </button>
            </div>
          }

          @if (calendarView() === 'month') {
            <table class="cue-datetimepicker__month-grid" role="grid">
              <thead>
                <tr>
                  @if (weekNumber()) {
                    <th class="cue-datetimepicker__week-heading" scope="col">#</th>
                  }
                  @for (weekday of weekdayLabels(); track $index) {
                    <th scope="col">{{ weekday }}</th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (row of calendarRows(); track row[0].date.getTime()) {
                  <tr>
                    @if (weekNumber()) {
                      <td class="cue-datetimepicker__week-number">
                        @if (weekNumberTemplate(); as customWeekNumber) {
                          <ng-container
                            [ngTemplateOutlet]="customWeekNumber"
                            [ngTemplateOutletContext]="weekNumberContext(row)"
                          />
                        } @else {
                          {{ weekNumberContext(row).weekNumber }}
                        }
                      </td>
                    }
                    @for (cell of row; track cell.date.getTime()) {
                      <td role="gridcell" [attr.aria-selected]="cell.selected">
                        <button
                          data-calendar-cell
                          class="cue-datetimepicker__calendar-cell"
                          type="button"
                          [class.cue-datetimepicker__calendar-cell--outside]="cell.outsideCurrentPeriod"
                          [class.cue-datetimepicker__calendar-cell--selected]="cell.selected"
                          [class.cue-datetimepicker__calendar-cell--today]="cell.today"
                          [attr.aria-label]="dateAriaLabel(cell.date)"
                          [attr.aria-current]="cell.today ? 'date' : null"
                          [disabled]="cell.disabled || !cell.label"
                          (click)="selectCalendarCell(cell)"
                          (keydown)="moveCalendarFocus($event)"
                        >
                          @if (templateForCurrentView(); as customCell) {
                            <ng-container
                              [ngTemplateOutlet]="customCell"
                              [ngTemplateOutletContext]="cellContext(cell)"
                            />
                          } @else {
                            {{ cell.label }}
                          }
                        </button>
                      </td>
                    }
                  </tr>
                }
              </tbody>
            </table>
          } @else {
            <div class="cue-datetimepicker__period-grid" role="grid">
              @for (cell of periodCells(); track cell.date.getTime()) {
                <button
                  data-calendar-cell
                  class="cue-datetimepicker__period-cell"
                  type="button"
                  role="gridcell"
                  [class.cue-datetimepicker__period-cell--outside]="cell.outsideCurrentPeriod"
                  [class.cue-datetimepicker__period-cell--selected]="cell.selected"
                  [attr.aria-current]="cell.today ? 'date' : null"
                  [attr.aria-selected]="cell.selected"
                  [disabled]="cell.disabled"
                  (click)="selectCalendarCell(cell)"
                  (keydown)="moveCalendarFocus($event)"
                >
                  @if (templateForCurrentView(); as customCell) {
                    <ng-container [ngTemplateOutlet]="customCell" [ngTemplateOutletContext]="cellContext(cell)" />
                  } @else {
                    {{ cell.label }}
                  }
                </button>
              }
            </div>
          }

          @if (footer() || footerTemplate()) {
            <div class="cue-datetimepicker__calendar-footer">
              @if (footerTemplate(); as customFooter) {
                <ng-container [ngTemplateOutlet]="customFooter" />
              } @else {
                <button type="button" (click)="selectToday()">{{ effectiveMessages().today }}</button>
              }
            </div>
          }
        </div>
      } @else {
        <div class="cue-datetimepicker__time" role="tabpanel">
          @if (timeOnly()) {
            @for (column of timeColumns(); track column.part) {
              @if (!$first && column.part !== 'period') {
                <span class="cue-datetimepicker__time-separator" aria-hidden="true">:</span>
              }
              <div class="cue-datetimepicker__time-part">
                <span>{{ column.label }}</span>
                <cue-time-list
                  [label]="column.label"
                  [options]="column.options"
                  [value]="column.selected"
                  [enableMouseWheel]="enableMouseWheel()"
                  (valueChange)="updateTimePart(column.part, $event)"
                />
              </div>
            }
          } @else {
            <label class="cue-datetimepicker__time-part">
              <span>{{ effectiveMessages().hour }}</span>
              <select
                [value]="selectedHour()"
                (change)="selectTimePart('hour', $event)"
                (wheel)="handleTimeWheel($event, 'hour')"
              >
                @for (hour of hourOptions(); track hour) {
                  <option
                    [value]="hour"
                    [selected]="hour === selectedHour()"
                    [disabled]="isTimeOptionDisabled('hour', hour)"
                  >
                    {{ formatTimePart(hour) }}
                  </option>
                }
              </select>
            </label>

            <span class="cue-datetimepicker__time-separator" aria-hidden="true">:</span>

            <label class="cue-datetimepicker__time-part">
              <span>{{ effectiveMessages().minute }}</span>
              <select
                [value]="selectedMinute()"
                (change)="selectTimePart('minute', $event)"
                (wheel)="handleTimeWheel($event, 'minute')"
              >
                @for (minute of minuteOptions(); track minute) {
                  <option
                    [value]="minute"
                    [selected]="minute === selectedMinute()"
                    [disabled]="isTimeOptionDisabled('minute', minute)"
                  >
                    {{ formatTimePart(minute) }}
                  </option>
                }
              </select>
            </label>

            @if (showSeconds()) {
              <span class="cue-datetimepicker__time-separator" aria-hidden="true">:</span>
              <label class="cue-datetimepicker__time-part">
                <span>{{ effectiveMessages().second }}</span>
                <select
                  [value]="selectedSecond()"
                  (change)="selectTimePart('second', $event)"
                  (wheel)="handleTimeWheel($event, 'second')"
                >
                  @for (second of secondOptions(); track second) {
                    <option
                      [value]="second"
                      [selected]="second === selectedSecond()"
                      [disabled]="isTimeOptionDisabled('second', second)"
                    >
                      {{ formatTimePart(second) }}
                    </option>
                  }
                </select>
              </label>
            }

            @if (usesTwelveHourClock()) {
              <label class="cue-datetimepicker__time-part cue-datetimepicker__time-period">
                <span class="cue-datetimepicker__visually-hidden">AM/PM</span>
                <select [value]="selectedPeriod()" (change)="selectTimePart('period', $event)">
                  <option value="am" [selected]="selectedPeriod() === 'am'">AM</option>
                  <option value="pm" [selected]="selectedPeriod() === 'pm'">PM</option>
                </select>
              </label>
            }
          }
        </div>
      }
    </div>

    <footer class="cue-datetimepicker__actions">
      @if (timeOnly()) {
        <ng-container [ngTemplateOutlet]="acceptAction" />
      }
      @if (cancelButton()) {
        <button
          class="cue-datetimepicker__action cue-datetimepicker__action--secondary"
          type="button"
          (click)="cancelPendingValue()"
        >
          {{ effectiveMessages().cancel }}
        </button>
      }
      @if (!timeOnly()) {
        <ng-container [ngTemplateOutlet]="acceptAction" />
      }
      <ng-template #acceptAction>
        <button
          class="cue-datetimepicker__action cue-datetimepicker__action--primary"
          type="button"
          [disabled]="pendingAcceptDisabled()"
          (click)="acceptPendingValue()"
        >
          {{ effectiveMessages().accept }}
        </button>
      </ng-template>
    </footer>
  </section>
</ng-template>
